/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import axios from 'axios';
import { useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    KeyboardEvent,
    MouseEvent,
    useContext,
    useState,
} from 'react';
import styled from 'styled-components';

import { SubmissionErrorCode } from '@toucan/common/services/application/constants';
import { getConfig } from '@toucan/common/services/nextjs/settings';

import CheckmarkIcon from '@toucan/client/global/icons/checkmark.svg';
import MinusIcon from '@toucan/client/global/icons/minus.svg';
import PlusIcon from '@toucan/client/global/icons/plus.svg';
import ShoppingBasketCheckmarkIcon from '@toucan/client/global/icons/shopping-basket-eu-checkmark.svg';
import ShoppingBasketPlusCutoutIcon from '@toucan/client/global/icons/shopping-basket-eu-plus-cutout.svg';
import ShoppingBasketPlusIcon from '@toucan/client/global/icons/shopping-basket-plus.svg';
import ShoppingCartCheckmarkIcon from '@toucan/client/global/icons/shopping-cart-us-checkmark.svg';
import ShoppingCartIcon from '@toucan/client/global/icons/shopping-cart-us.svg';

import {
    Market,
    MarketCode,
    MarketContext,
} from '@toucan/client/global/market';
import { Product } from '@toucan/client/global/product';

import { BasketItem, useBasketMaxItemsError } from '@toucan/client/core/basket';
import { shouldKeyDownTriggerEvent } from '@toucan/client/core/events';

import { BasketContext } from '@toucan/client/shared/context/basket';

import { styles } from './add-to-basket-button.styles';

export type AddToBasketButtonProps = {
    /**
     * If a loading state should be displayed
     */
    isLoading?: boolean;

    /**
     * The product we are displaying
     */
    product?: Product;

    onAddToBasketError?: (error: unknown) => void;
    className?: string;
    'data-testid'?: string;
};

export type StyledAddToBasketButtonProps = {
    isInBasket: boolean;
    activeMarket: Market;
};

const StyledAddToBasketButton = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const AddToBasketButton: FunctionComponent<AddToBasketButtonProps> = ({
    isLoading,
    product,
    onAddToBasketError,
    className,
    ...otherProps
}: AddToBasketButtonProps) => {
    const { t } = useTranslation();
    const { addToBasket, basket, removeFromBasket } = useContext(BasketContext);
    const { activeMarket } = useContext(MarketContext);
    const { showBasketMaxItemsError } = useBasketMaxItemsError();
    const { publicRuntimeConfig } = getConfig();

    const [isUpdatingBasket, setIsUpdatingBasket] = useState(false);

    if (!isLoading && !product) {
        return null;
    }

    const { sku, id } = product || {};

    const isInBasket = basket?.items?.some(
        (item: BasketItem) => item.sku === sku || item.id === id
    );

    const basketItem = basket?.items?.find((item) => item.id === id);

    const labelText = isInBasket ? t('Remove from basket') : t('Add to basket');

    const disableInteractions = Boolean(isUpdatingBasket || isLoading);

    const basketMaxItems = publicRuntimeConfig?.basketMaxItems ?? 0;

    const handleButtonAction = async (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();

        const shouldAddToBasket = !isInBasket && product;
        const shouldRemoveFromBasket = isInBasket && basketItem;

        const shouldUpdateBasket = shouldAddToBasket || shouldRemoveFromBasket;

        if (disableInteractions || !shouldUpdateBasket) {
            return;
        }

        setIsUpdatingBasket(true);

        if (shouldAddToBasket) {
            try {
                await addToBasket(product, 1);
            } catch (error: unknown) {
                if (
                    axios.isAxiosError(error) &&
                    error.response?.data?.errorCode ===
                        SubmissionErrorCode.BASKET_MAX_ITEM_LIMIT_REACHED &&
                    basketMaxItems > 0
                ) {
                    showBasketMaxItemsError({
                        isClosable: true,
                        basketMaxItems,
                    });
                } else {
                    onAddToBasketError?.(error);
                }
            }
        }

        if (shouldRemoveFromBasket) {
            removeFromBasket(basketItem, 1);
        }

        setIsUpdatingBasket(false);
    };

    const handleKeyDownEvent = (event: KeyboardEvent<HTMLElement>) => {
        if (shouldKeyDownTriggerEvent(event)) {
            handleButtonAction(event);
        }
    };

    const BasketIcon =
        activeMarket.code === MarketCode.EN_US
            ? ShoppingCartIcon
            : ShoppingBasketPlusCutoutIcon;

    const BasketCheckmarkIcon =
        activeMarket.code === MarketCode.EN_US
            ? ShoppingCartCheckmarkIcon
            : ShoppingBasketCheckmarkIcon;

    const renderAddToBasketIcons = () => {
        return (
            <Fragment>
                <BasketIcon className="icon basket" />
                <ShoppingBasketPlusIcon className="icon basket-plus" />
                <PlusIcon className="icon plus" />
            </Fragment>
        );
    };

    const renderRemoveFromBasketIcons = () => {
        return (
            <Fragment>
                <BasketCheckmarkIcon className="icon basket-checkmark" />
                <CheckmarkIcon className="icon checkmark" />
                <MinusIcon className="icon minus" />
            </Fragment>
        );
    };

    return (
        <StyledAddToBasketButton
            {...otherProps}
            isInBasket={isInBasket}
            activeMarket={activeMarket}
            onClick={handleButtonAction}
            onKeyDown={handleKeyDownEvent}
            className={`icon-container${className ? ` ${className}` : ''}`}
            tabIndex={0}
            role="button"
            aria-label={labelText}
            data-testid="product-card__basket-button"
            aria-disabled={disableInteractions}
        >
            <Icon
                variant={IconVariant.ACTIONABLE}
                size={IconSize.L}
                isLoading={isLoading}
                isToggled={isInBasket}
            >
                {isInBasket
                    ? renderRemoveFromBasketIcons()
                    : renderAddToBasketIcons()}
            </Icon>
        </StyledAddToBasketButton>
    );
};
